import React, { useState } from 'react';
import { CheckCircle, Shield, Clock, DollarSign, FileText, Users, Award, TrendingUp, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const CopyrightRegistrationPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: Shield,
      title: "Legal Protection",
      description: "Establish legal proof of ownership and gain exclusive rights to reproduce, distribute, and display your creative work."
    },
    {
      icon: DollarSign,
      title: "Monetary Damages",
      description: "Recover statutory damages up to $150,000 per work and attorney's fees in infringement lawsuits."
    },
    {
      icon: FileText,
      title: "Public Record",
      description: "Create a public record of your copyright claim, making it easier to prove ownership in disputes."
    },
    {
      icon: Award,
      title: "Presumption of Validity",
      description: "Registered copyrights are presumed valid in court, shifting the burden of proof to infringers."
    },
    {
      icon: Users,
      title: "Licensing Opportunities",
      description: "Registration makes it easier to license your work and collect royalties from authorized use."
    },
    {
      icon: TrendingUp,
      title: "Increased Value",
      description: "Registered copyrights are valuable business assets that increase your company's worth."
    }
  ];

  const process = [
    {
      step: "1",
      title: "Consultation & Assessment",
      description: "We review your creative works, determine the best registration category, and develop a filing strategy.",
      timeline: "1-2 days"
    },
    {
      step: "2",
      title: "Application Preparation",
      description: "Our team prepares all required documentation, deposit copies, and forms for submission to the Copyright Office.",
      timeline: "3-5 days"
    },
    {
      step: "3",
      title: "Filing & Registration",
      description: "We file your application with the U.S. Copyright Office, track its progress, and deliver your official registration certificate.",
      timeline: "3-8 months"
    }
  ];

  const packages = [
    {
      name: "Basic",
      price: "$199",
      description: "For single creative works",
      features: [
        "Copyright application filing",
        "Single work registration",
        "Electronic submission",
        "Basic application review",
        "Certificate delivery",
        "Email support"
      ],
      popular: false
    },
    {
      name: "Professional",
      price: "$299",
      description: "Most popular choice",
      features: [
        "Everything in Basic",
        "Comprehensive work review",
        "Copyright search",
        "Office action responses",
        "Expedited processing option",
        "Priority support",
        "Certificate tracking"
      ],
      popular: true
    },
    {
      name: "Portfolio",
      price: "$499",
      description: "For multiple works",
      features: [
        "Everything in Professional",
        "Up to 5 work registrations",
        "Portfolio management",
        "Infringement monitoring (1 year)",
        "Licensing consultation",
        "Dedicated account manager",
        "24/7 support"
      ],
      popular: false
    }
  ];

  const protectableWorks = [
    {
      category: "Literary Works",
      examples: ["Books", "Articles", "Poetry", "Software code", "Website content"]
    },
    {
      category: "Visual Arts",
      examples: ["Paintings", "Photographs", "Sculptures", "Graphics", "Illustrations"]
    },
    {
      category: "Performing Arts",
      examples: ["Music", "Lyrics", "Choreography", "Scripts", "Plays"]
    },
    {
      category: "Audiovisual",
      examples: ["Films", "Videos", "TV shows", "Animations", "Video games"]
    },
    {
      category: "Sound Recordings",
      examples: ["Albums", "Singles", "Podcasts", "Audio books", "Sound effects"]
    },
    {
      category: "Architectural",
      examples: ["Building designs", "Blueprints", "Floor plans", "3D models"]
    }
  ];

  const faqs = [
    {
      question: "What is copyright registration and why do I need it?",
      answer: "Copyright registration is the official recording of your creative work with the U.S. Copyright Office. While copyright exists automatically upon creation, registration provides crucial legal benefits including the ability to sue for infringement, eligibility for statutory damages and attorney's fees, and creates a public record of your ownership. It's essential for protecting your creative assets and monetizing your work."
    },
    {
      question: "How long does the copyright registration process take?",
      answer: "Standard copyright registration typically takes 3-8 months for the Copyright Office to process. However, expedited processing is available for an additional fee and can reduce this to 5-7 business days. We monitor your application throughout the process and notify you immediately when your certificate is issued."
    },
    {
      question: "What can be copyrighted?",
      answer: "Copyright protects original works of authorship including literary works, musical compositions, dramatic works, choreography, pictorial and graphic works, sculptures, motion pictures, sound recordings, and architectural works. The work must be fixed in a tangible form and contain a minimum amount of creativity. Ideas, facts, and titles generally cannot be copyrighted."
    },
    {
      question: "How long does copyright protection last?",
      answer: "For works created after January 1, 1978, copyright lasts for the life of the author plus 70 years. For works made for hire, anonymous works, or pseudonymous works, protection lasts 95 years from publication or 120 years from creation, whichever is shorter. This provides long-term protection for your creative works."
    },
    {
      question: "Do I need to register my copyright immediately after creation?",
      answer: "While copyright exists from the moment of creation, early registration provides maximum legal benefits. You must register before filing an infringement lawsuit, and registering within 3 months of publication or before infringement occurs allows you to claim statutory damages and attorney's fees. We recommend registering as soon as possible after completing your work."
    },
    {
      question: "Can I register multiple works together?",
      answer: "Yes, under certain conditions you can register multiple works as a collection or through our Portfolio package. This can be more cost-effective than individual registrations. We'll assess your works and recommend the best registration strategy based on your needs and budget."
    },
    {
      question: "What's the difference between copyright and trademark?",
      answer: "Copyright protects original creative works (books, music, art, etc.), while trademarks protect brand names, logos, and slogans used in commerce. Copyright exists automatically upon creation but registration provides additional benefits. Trademarks must be used in commerce and registered with the USPTO for full federal protection."
    },
    {
      question: "What happens if someone infringes my copyright?",
      answer: "With a registered copyright, you can file a lawsuit in federal court seeking injunctions to stop the infringement, monetary damages including actual damages and infringer's profits, or statutory damages up to $150,000 per work for willful infringement. You can also recover attorney's fees and costs, making it financially feasible to enforce your rights."
    },
    {
      question: "How much does it cost to register a copyright?",
      answer: "Our copyright registration services start at $199 for basic registration, which includes our professional filing service plus the Copyright Office filing fee. We offer comprehensive packages starting at $299 that include copyright searches, office action responses, and priority support. The investment protects your creative work and provides legal recourse against infringers."
    },
    {
      question: "Can I register a copyright for work I created years ago?",
      answer: "Yes, you can register copyright for any unpublished work or published work that's still within the copyright term. However, registration must occur within 3 months of publication (or before infringement) to be eligible for statutory damages and attorney's fees. We recommend registering older works to establish a public record and gain litigation rights."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Copyright Registration Services - Protect Your Creative Works"
        description="Copyright registration for authors, artists, and creators. Secure your creative works with expert guidance."
        keywords="copyright registration, creative work protection, copyright filing, author rights, artist protection, music copyright"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Protect Your Creative Works with Copyright Registration
              </h1>
              <p className="text-xl mb-8 text-blue-100">
                Secure legal protection for your original works. From books and music to software and art, safeguard your creative assets with professional copyright registration services.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Start Registration Now
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Expert
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>3-8 Month Processing</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Lifetime Protection</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Why Register Your Copyright?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Legal Presumption of Ownership</strong>
                    <span className="text-gray-300">Registration creates a public record establishing you as the copyright owner</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Sue for Infringement</strong>
                    <span className="text-gray-300">Required before filing copyright infringement lawsuits in federal court</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Statutory Damages</strong>
                    <span className="text-gray-300">Claim up to $150,000 per work plus attorney's fees without proving actual damages</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of Copyright Registration
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive protection and legal advantages for your creative works
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Can Be Copyrighted */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Can You Copyright?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Copyright protects a wide range of original creative works
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {protectableWorks.map((category, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-4 pb-3 border-b-2 border-[#FF035B] group-hover:border-white">
                  {category.category}
                </h3>
                <ul className="space-y-2">
                  {category.examples.map((example, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700 group-hover:text-white/90 transition-colors duration-500">
                      <div className="w-1.5 h-1.5 bg-[#FF035B] group-hover:bg-white rounded-full transition-colors duration-500"></div>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Registration Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, transparent, and expertly managed from start to finish
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing showPrices={false} title="Copyright Registration Services" subtitle="Get a personalized quote for your specific needs" />


      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about copyright registration
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Creative Work?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Don't wait until it's too late. Secure your copyright protection today and gain peace of mind knowing your creative works are legally protected.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Start Registration Now
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default CopyrightRegistrationPage;
